import { Box } from '@mui/material';
import { useCallback, useEffect } from 'react';
import type { CohortHistoryChartConfig } from '@/features/chartsView';
import { useRequestConfig } from '@/features/chartsView';
import { useAppDispatch, useAppSelector } from '@/shared/model';
import { Loader } from '@/shared/ui';
import {
  getChartData,
  initMeta,
  selectMetaIsLoaded,
  selectMetaIsLoading,
} from '../../model/slice';
import type { CohortHistoryChartDataResponse } from '../../model/types';
import { Chart } from '../Chart/Chart';

interface Props {
  preset: unknown;
  onDataFetchStart?: () => void;
  onDataFetchFinish?: (data?: CohortHistoryChartDataResponse) => void;
}

export function Widget({ preset, onDataFetchStart, onDataFetchFinish }: Props) {
  const dispatch = useAppDispatch();
  const metaIsLoading = useAppSelector(selectMetaIsLoading);
  const metaIsLoaded = useAppSelector(selectMetaIsLoaded);
  const config = preset as CohortHistoryChartConfig;

  const requestConfig = useRequestConfig(config);

  const onGetData = useCallback(
    () => {
      onDataFetchStart?.();

      return dispatch(getChartData(config));
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestConfig, onDataFetchStart, dispatch],
  );

  useEffect(() => {
    dispatch(initMeta());
  }, [dispatch]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        position: 'relative',
        width: '100%',
        display: 'flex',
      }}
    >
      <Loader active={metaIsLoading} />
      {metaIsLoaded && (
        <Chart
          config={config}
          onGetData={onGetData}
          onDataChange={onDataFetchFinish}
        />
      )}
    </Box>
  );
}
